import { useLayoutEffect, useMemo, useRef, useState, type TextareaHTMLAttributes } from 'react';
import { api } from '../api';
import type { MediaItem, Story } from '../types';

/** Same cap as the server stores (story.service.ts LINE_MAX). */
const LINE_MAX = 220;

interface Draft {
  en: string;
  ur: string;
}

interface Props {
  episodeId: string;
  items: MediaItem[];
  story: Story;
  onSaved: (story: Story) => void;
  onCancel: () => void;
}

function thumbOf(item: MediaItem): string | null {
  return item.type === 'VIDEO' ? item.posterUrl : (item.thumbnailUrl ?? item.url);
}

/**
 * Edit the story's words: opening, closing, and a line for any memory (change,
 * clear or add one). Whatever the user writes or changes is theirs, and
 * "Write it again" keeps it.
 */
export default function StoryEditor({ episodeId, items, story, onSaved, onCancel }: Props) {
  const [opening, setOpening] = useState<Draft>({ en: story.opening.en, ur: story.opening.ur });
  const [closing, setClosing] = useState<Draft>({ en: story.closing.en, ur: story.closing.ur });
  // One entry per memory that has (or is getting) a line.
  const [lines, setLines] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(story.lines.map((l) => [l.mediaId, { en: l.en, ur: l.ur }])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const original = useMemo(() => new Map(story.lines.map((l) => [l.mediaId, l])), [story]);

  const dirty = useMemo(() => {
    const same = (a: Draft, b: Draft) => a.en.trim() === b.en.trim() && a.ur.trim() === b.ur.trim();
    if (!same(opening, story.opening) || !same(closing, story.closing)) return true;
    const ids = Object.keys(lines).filter((id) => lines[id].en.trim() || lines[id].ur.trim());
    if (ids.length !== story.lines.length) return true;
    return ids.some((id) => !original.has(id) || !same(lines[id], original.get(id)!));
  }, [opening, closing, lines, story, original]);

  const setLine = (id: string, patch: Partial<Draft>) =>
    setLines((all) => ({ ...all, [id]: { ...(all[id] ?? { en: '', ur: '' }), ...patch } }));
  const dropLine = (id: string) =>
    setLines((all) => {
      const next = { ...all };
      delete next[id];
      return next;
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.editStory(episodeId, {
        opening,
        closing,
        // Episode order; empty lines are dropped by the server.
        lines: items.filter((i) => lines[i.id]).map((i) => ({ mediaId: i.id, ...lines[i.id] })),
      });
      if (r.story) onSaved(r.story);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl bg-white/[0.04] p-4 ring-1 ring-white/10 sm:p-5">
      <p className="mb-4 text-sm text-white/55">
        Change any words, clear a line you don’t want, or add one to any memory. Lines you write or change stay when the
        story is written again.
      </p>

      <BilingualFields label="Opening" value={opening} onChange={(p) => setOpening((o) => ({ ...o, ...p }))} />

      <ol className="mt-5 space-y-3" aria-label="Lines for each memory">
        {items.map((item, i) => {
          const draft = lines[item.id];
          const thumb = thumbOf(item);
          return (
            <li key={item.id} className="flex gap-3 rounded-lg bg-black/20 p-2.5">
              <div className="relative h-16 w-16 flex-none overflow-hidden rounded-md bg-white/10">
                {thumb && <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}
                <span className="absolute top-0.5 left-0.5 rounded bg-black/70 px-1 text-[10px] font-semibold">{i + 1}</span>
              </div>
              <div className="min-w-0 flex-1">
                {draft ? (
                  <>
                    <BilingualFields
                      label={`Memory ${i + 1}`}
                      hideLabel
                      value={draft}
                      onChange={(p) => setLine(item.id, p)}
                    />
                    <button
                      onClick={() => dropLine(item.id)}
                      className="focus-ring mt-1 rounded text-xs text-white/40 hover:text-white/80"
                      aria-label={`Remove the line for memory ${i + 1}`}
                    >
                      Remove line
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setLine(item.id, {})}
                    className="focus-ring mt-4 rounded text-sm text-white/45 hover:text-white"
                    aria-label={`Add a line for memory ${i + 1}`}
                  >
                    + Add a line
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-5">
        <BilingualFields label="Ending" value={closing} onChange={(p) => setClosing((c) => ({ ...c, ...p }))} />
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <div className="sticky bottom-0 -mx-4 mt-5 flex flex-wrap items-center gap-3 border-t border-white/10 bg-ink-950/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:-mx-5 sm:px-5">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="focus-ring rounded-md bg-white px-5 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button onClick={onCancel} disabled={saving} className="focus-ring rounded-md px-3 py-2 text-sm text-white/60 hover:text-white">
          Cancel
        </button>
        {!dirty && <span className="text-xs text-white/30">No changes yet</span>}
      </div>
    </div>
  );
}

/** English input and Roman Urdu input (plain left-to-right text) for one line. */
function BilingualFields({
  label,
  hideLabel = false,
  value,
  onChange,
}: {
  label: string;
  hideLabel?: boolean;
  value: Draft;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const field =
    'focus-ring block w-full resize-none overflow-hidden rounded-md bg-black/30 px-3 py-2 text-sm text-white ring-1 ring-white/10 placeholder:text-white/25 focus:ring-white/30';
  return (
    <fieldset>
      <legend className={hideLabel ? 'sr-only' : 'mb-1.5 text-xs font-semibold tracking-wide text-white/45 uppercase'}>{label}</legend>
      <AutoTextarea
        value={value.en}
        onChange={(e) => onChange({ en: e.target.value })}
        maxLength={LINE_MAX}
        placeholder="English"
        aria-label={`${label}, English`}
        className={field}
      />
      <AutoTextarea
        value={value.ur}
        onChange={(e) => onChange({ ur: e.target.value })}
        maxLength={LINE_MAX}
        lang="ur"
        placeholder="اردو"
        aria-label={`${label}, Urdu`}
        className={`${field} mt-1.5 text-base leading-relaxed`}
      />
    </fieldset>
  );
}

/** A textarea that grows to fit its text, so long lines are never hidden. */
function AutoTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={1} {...props} />;
}
